/**
 * Global Imports
*/

import { ChatUserstate } from 'tmi.js';

/**
 * Config Imports
*/

import { ChoosedayConfig } from '@config/Chooseday';
import { TwitchConfig } from '@config/Twitch';

/**
 * System Imports
*/

import { TwitchWriteMessage } from '@system/Twitch';
import { User, UserGetName, UserIsModerator } from '@system/User';
import { RandomInt } from '@system/Utility';

/**
 * Relative Imports
*/

import { ChoosedayState, ChoosedayRoll, ChoosedayRolls } from './types';

/**
 * Locals
*/

let state: ChoosedayState = ChoosedayState.Idle;
let rolls: ChoosedayRolls = [];
let tie_roll: number = -1;
let tie_end_index: number = 1;

/**
 * Private Functions
*/

/**
 * @return {void}
 */
function _normalRoll(
    channel: string,
    user: User | ChatUserstate): void
{
    for (const roll of rolls)
    {
        if (UserGetName(user) === roll.user)
        {
            return TwitchWriteMessage(channel, `You've already rolled, ${ UserGetName(user) }.`);
        }
    }

    const roll = RandomInt(ChoosedayConfig.roll.min, ChoosedayConfig.roll.max);

    rolls.push({
        roll,
        user: UserGetName(user),
    });

    TwitchWriteMessage(channel, `${ UserGetName(user) } rolled a${ (roll === 8 || roll === 11 || roll === 18 || (roll >= 80 && roll <= 89)) ? 'n' : '' } ${ roll }`);

    if (roll === 69)
    {
        TwitchWriteMessage(channel, 'Nice');
    }
}

/**
 * @return {void}
 */
function _tiebreakRoll(
    channel: string,
    user: User | ChatUserstate): void
{
    let index = -1;

    for (let i = 0; i <= tie_end_index; i++)
    {
        if (rolls[i].user === UserGetName(user))
        {
            index = i;
            break;
        }
    }

    if (index === -1)
    {
        return TwitchWriteMessage(channel, `You're not in the tiebreaker, ${ UserGetName(user) }.`);
    }
    else if (rolls[index].roll !== tie_roll)
    {
        return TwitchWriteMessage(channel, `You've already rolled, ${ UserGetName(user) }.`);
    }

    const roll = RandomInt(ChoosedayConfig.roll.min, ChoosedayConfig.roll.max);

    rolls[index].roll += roll;

    TwitchWriteMessage(channel, `${ UserGetName(user) } rolled a${ (roll === 8 || roll === 11 || roll === 18 || (roll >= 80 && roll <= 89)) ? 'n' : '' } ${ roll }`);

    if (roll === 69)
    {
        TwitchWriteMessage(channel, 'Nice');
    }
}

/**
 * @return {void}
 */
function _startTieBreak(): void
{
    state = ChoosedayState.TieBreak;
    tie_roll = rolls[0].roll;

    TwitchWriteMessage(TwitchConfig.channel, 'Tiebreaker started. Chat !roll to join.');
}

/**
 * @return {void}
 */
function ChoosedayStartPick(
    channel: string,
    user: User | ChatUserstate): void
{
    state = ChoosedayState.Pick;
    rolls = [];
    tie_roll = -1;
    tie_end_index = -1;

    TwitchWriteMessage(channel, 'Chooseday round started. Chat !roll to join.');
}

/**
 * @return {void}
 */
function ChoosedayEndPick(
    channel: string,
    user: User | ChatUserstate): void
{
    state = ChoosedayState.Play;

    const roll_count = rolls.length;

    if (roll_count === 0)
    {
        return TwitchWriteMessage(channel, 'Chooseday rolling is over but nobody rolled.');
    }
    else if (roll_count === 1)
    {
        return TwitchWriteMessage(channel, `Chooseday is over and ${ rolls[0].user } is the winner by default.`);
    }

    rolls = rolls.sort((a, b) => b.roll - a.roll);

    let tie_detected = false;

    for (let i = 0; i < roll_count; i++)
    {
        if (tie_detected)
        {
            if (rolls[i].roll !== rolls[i + 1]?.roll)
            {
                tie_end_index = i;
                break;
            }
        }
        else if (typeof rolls[i + 1]?.roll === 'number' && rolls[i].roll === rolls[i + 1].roll && rolls[i + 1].roll === rolls[0].roll)
        {
            tie_detected = true;
        }
    }

    if (tie_detected)
    {
        TwitchWriteMessage(channel, `Hold on to your butts. We have a tie! ${ rolls.slice(0, tie_end_index + 1).map(itr => itr.user).join(', ') } rolled a ${ rolls[0].roll }.`);
        _startTieBreak();
    }
    else
    {
        TwitchWriteMessage(channel, `Chooseday is over. The winner is ${ rolls[0].user } with a ${ rolls[0].roll } roll.`);
    }
}

/**
 * Public Functions
*/

/**
 * @return {void}
 */
export function ChooseHandleToggle(
    channel: string,
    user: User | ChatUserstate,
    args: string): void
{
    if (!UserIsModerator(user))
    {
        return TwitchWriteMessage(channel, `You can't do that, ${ UserGetName(user) }.`);
    }

    switch ((args || '').trim().toLowerCase())
    {
    case '0':
    case 'stop':
    case 'end':
    case 'fin':
    case 'finish':
    case 'done':
    case 'complete':
    case 'over':
        ChoosedayEndPick(channel, user);
        break;
    case '1':
    case 'start':
    case 'begin':
    case 'activate':
    case 'init':
    case 'initiate':
        ChoosedayStartPick(channel, user);
        break;
    default:
        TwitchWriteMessage(channel, `Invalid argument given.`);
        break;
    }
}

/**
 * @return {void}
 */
export function ChoosedayHandleRoll(
    channel: string,
    user: User | ChatUserstate): void
{
    switch (state)
    {
    case ChoosedayState.Pick:
        _normalRoll(channel, user);
        break;
    case ChoosedayState.TieBreak:
        _tiebreakRoll(channel, user);
        break;
    default:
        TwitchWriteMessage(channel, `Chooseday rolling has not started, ${ UserGetName(user) }.`);
    }
}

/**
 * @return {void}
 */
export function ChoosedayHandleRolls(
    channel: string,
    user: User | ChatUserstate): void
{
    const sorted = rolls.sort((a, b) => b.roll - a.roll);
    const parts = sorted.map(itr => `${ itr.user }: ${ itr.roll }`).join(', ');

    TwitchWriteMessage(channel, `Chooseday standings are: ${ parts }.`);
}
